import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Popover,
  Skeleton,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ChatService from '../ChatService/ChatService';
import './ChatWindow.css';
import { axiosInstance } from '../../../utils/tokenUtils';
import LoadingScreen from '../../LoadingIcon/LoadingScreen';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendIconSvg from '../../../assets/ChatIcons/send.svg';
import ChatServiceApi from '../ChatService/ChatService';

// Chat Skeleton Component for shimmer loading effect
const ChatSkeleton = () => {
  return (
    <Box className="chat-skeleton-container">
      {/* Simulate 4 messages with shimmer effect */}
      {[1, 2, 3, 4].map((item, index) => (
        <Box
          key={item}
          className={`chat-skeleton-message ${index % 2 === 0 ? 'received' : 'sent'}`}
        >
          <Box className="chat-skeleton-avatar">
            <Skeleton variant="circular" width={32} height={32} animation="wave" />
          </Box>
          <Box className="chat-skeleton-content">
            <Skeleton
              variant="rounded"
              width={index % 2 === 0 ? 200 : 250}
              height={40}
              animation="wave"
            />
            <Skeleton variant="text" width={60} height={15} animation="wave" />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const ChatWindow = ({ messages, loading, selectedChat, onMessageSent }) => {
  const messagesEndRef = useRef(null);
  const scrollableContainerRef = useRef(null);
  const messageGroupRefs = useRef({});
  const [groupedMessages, setGroupedMessages] = useState({});
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isScrollingEnabled, setIsScrollingEnabled] = useState(true);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [previewModal, setPreviewModal] = useState({
    open: false,
    fileUrl: null,
    fileName: null,
    fileType: null,
  });

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await axiosInstance.get('/auth/users/me');
        setCurrentUser(response.data);
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    // Revoke object URL when file changes or on unmount
    return () => {
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    };
  }, [filePreviewUrl]);

  // Enhanced scroll to bottom with MutationObserver to ensure it works when new messages arrive
  useEffect(() => {
    if (isScrollingEnabled) {
      scrollToBottom('auto');
    }
  }, [messages, isScrollingEnabled]);

  // Watch for content changes in the scrollable container
  useEffect(() => {
    if (!scrollableContainerRef.current) return;

    const observer = new MutationObserver(() => {
      if (isScrollingEnabled) {
        scrollToBottom('auto');
      }
    });

    observer.observe(scrollableContainerRef.current, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [isScrollingEnabled]);

  // Detect user scroll to temporarily disable auto-scrolling
  useEffect(() => {
    const container = scrollableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 50;
      setIsScrollingEnabled(isAtBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Group messages by date when messages change
    if (messages && messages.length > 0) {
      // Filter out empty messages before grouping
      const validMessages = messages.filter(message => {
        const hasText = typeof message.message === 'string' && message.message.trim() !== '';
        const hasFile = Boolean(message.file_url);
        return hasText || hasFile;
      });

      groupMessagesByDate(validMessages);

      // Collect all dates that have messages - ensure proper UTC to local conversion
      const dates = validMessages.map(m => {
        const utcDate = m.created_at.endsWith('Z') ? m.created_at : `${m.created_at}Z`;
        return new Date(utcDate);
      });

      // Create a set of unique dates in ISO format to eliminate duplicates
      const uniqueDatesISO = [...new Set(dates.map(d => d.toISOString().split('T')[0]))];

      // Convert the ISO dates to dayjs objects
      setAvailableDates(uniqueDatesISO.map(d => dayjs(d)));
    }
  }, [messages]);

  // Connect via ChatService whenever selectedChat changes
  useEffect(() => {
    if (!selectedChat) return;

    const ws = ChatService.connectWebSocket(selectedChat.id, data => {
      if (data.type === 'new_message') {
        onMessageSent(data);
        setIsScrollingEnabled(true);
      } else if (data.type === 'message_sent') {
        console.log('Message sent confirmation:', data);
      } else if (data.type === 'message_delivered') {
        console.log('Message delivered:', data);
      } else if (data.type === 'message_read') {
        console.log('Message read:', data);
      }
    });

    // Cleanup on unmount or when chat changes
    // return () => {
    //   ChatService.closeWebSocket(selectedChat.id);
    // };
  }, [selectedChat]);

  // Enhanced scrollToBottom with behavior parameter
  const scrollToBottom = (behavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  };

  const formatTime = dateString => {
    // Create a date object and ensure it's treated as UTC
    // First ensure dateString is in ISO format by appending 'Z' if missing
    const utcDate = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    const date = new Date(utcDate);

    // Format in local time (hour12:true ensures AM/PM format)
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  };

  const formatDateHeader = dateString => {
    const utcDate = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    const date = dayjs(utcDate);
    return date.format('DD MMMM YYYY');
  };

  const isCurrentUser = message => {
    const isSenderMatch =
      message?.sender_id && selectedChat?.teacher_id
        ? String(message.sender_id) === String(selectedChat.teacher_id)
        : false;
    return message.is_sent === true || isSenderMatch;
  };

  // Get the sender name initial for avatar
  const getSenderInitial = message => {
    if (isCurrentUser(message)) {
      // Current user's message - use current user's first name initial
      return currentUser?.first_name?.charAt(0) || 'Me';
    } else {
      // Recipient's message - use recipient's first name initial
      return selectedChat.recipient_details?.first_name?.charAt(0) || '?';
    }
  };

  const groupMessagesByDate = messageList => {
    // Sort messages by date/time (oldest to newest)
    const sortedMessages = [...messageList].sort((a, b) => {
      // Ensure dates are treated as UTC
      const dateA = a.created_at.endsWith('Z') ? a.created_at : `${a.created_at}Z`;
      const dateB = b.created_at.endsWith('Z') ? b.created_at : `${b.created_at}Z`;
      return new Date(dateA) - new Date(dateB);
    });

    // Group messages by date in local timezone
    const grouped = {};
    sortedMessages.forEach(message => {
      // Skip empty messages without file attachments
      if ((!message.message || message.message.trim() === '') && !message.file_url) {
        return;
      }

      // Ensure date is treated as UTC and then converted to local date string
      const utcDate = message.created_at.endsWith('Z')
        ? message.created_at
        : `${message.created_at}Z`;
      const localDate = new Date(utcDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      if (!grouped[localDate]) {
        grouped[localDate] = [];
      }
      grouped[localDate].push(message);
    });

    setGroupedMessages(grouped);
  };

  const handleOpenPreview = (fileUrl, fileName) => {
    const fileExtension = fileName?.split('.').pop().toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileExtension);
    const isPdf = fileExtension === 'pdf';

    setPreviewModal({
      open: true,
      fileUrl: fileUrl,
      fileName: fileName,
      fileType: isImage ? 'image' : isPdf ? 'pdf' : 'other',
    });
  };

  const handleClosePreview = () => {
    setPreviewModal({
      open: false,
      fileUrl: null,
      fileName: null,
      fileType: null,
    });
  };

  const handleDownloadFromPreview = async () => {
    if (!previewModal.fileUrl) return;

    try {
      const link = document.createElement('a');
      link.href = previewModal.fileUrl;
      link.download = previewModal.fileName || 'download';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      try {
        window.open(previewModal.fileUrl, '_blank');
      } catch (fallbackError) {
        console.error('Fallback download also failed:', fallbackError);
      }
    }
  };

  const renderFileMessage = message => {
    const fileExtension = message.file_name?.split('.').pop().toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileExtension)) {
      return (
        <Box className="kw-image-bubble">
          <img
            src={message.file_url || '/placeholder.svg'}
            alt={message.file_name}
            className="kw-image"
            onClick={() => handleOpenPreview(message.file_url, message.file_name)}
            style={{ cursor: 'pointer' }}
            title="Click to preview"
          />
        </Box>
      );
    }

    const name = message.file_name || 'file';
    const sizeText = message.file_size ? formatFileSize(message.file_size) : '';
    const isImageUrl = /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);

    return (
      <Box
        className="kw-file-bubble"
        onClick={() => handleOpenPreview(message.file_url, message.file_name)}
        style={{ cursor: 'pointer' }}
        title="Click to preview"
      >
        {isImageUrl ? (
          <img src={message.file_url} alt={name} className="kw-file-thumb" />
        ) : (
          <Box
            className="kw-file-thumb"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            📄
          </Box>
        )}
        <Box className="kw-file-meta">
          <Typography
            className="kw-file-name"
            onClick={e => {
              e.stopPropagation();
              handleOpenPreview(message.file_url, message.file_name);
            }}
            style={{ cursor: 'pointer' }}
          >
            {name}
          </Typography>
          {sizeText && <Typography className="kw-file-size">{sizeText}</Typography>}
        </Box>
      </Box>
    );
  };

  // Handle opening the calendar
  const handleDateClick = event => {
    setCalendarAnchorEl(event.currentTarget);
  };

  // Handle closing the calendar
  const handleCalendarClose = () => {
    setCalendarAnchorEl(null);
  };

  // Handle selecting a date from the calendar
  const handleDateSelect = newDate => {
    setSelectedDate(newDate);
    handleCalendarClose();
    scrollToDate(newDate);
  };

  // Scroll to the selected date or nearest available date
  const scrollToDate = selectedDate => {
    // Convert the dayjs date to a localized date string for comparison
    const localDateStr = selectedDate.toDate().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    // If the exact date exists
    if (messageGroupRefs.current[localDateStr]) {
      messageGroupRefs.current[localDateStr].scrollIntoView({
        behavior: 'smooth',
      });
      return;
    }

    // Find nearest date
    const allDates = Object.keys(messageGroupRefs.current);

    if (allDates.length === 0) return;

    // Use toDate() to convert dayjs objects to JS Date objects
    const targetDateObj = selectedDate.toDate();
    const targetTime = targetDateObj.getTime();

    // For each date key, parse it back to a date and get its time
    const dateTimeMap = allDates.map(dateStr => {
      let date;
      try {
        // Parse the localized date string back to a date object
        const parts = dateStr.split(' ');
        const month = new Date(Date.parse(`${parts[0]} 1, 2000`)).getMonth();
        const day = parseInt(parts[1].replace(',', ''));
        const year = parseInt(parts[2]);
        date = new Date(year, month, day);
      } catch (e) {
        console.error('Error parsing date:', e);
        return { dateStr, time: 0 };
      }
      return { dateStr, time: date.getTime() };
    });

    // Find the closest date
    const closest = dateTimeMap.reduce((prev, curr) => {
      return Math.abs(curr.time - targetTime) < Math.abs(prev.time - targetTime) ? curr : prev;
    });

    if (messageGroupRefs.current[closest.dateStr]) {
      messageGroupRefs.current[closest.dateStr].scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  // Add useEffect to add indicators for dates with messages after calendar renders
  useEffect(() => {
    if (calendarAnchorEl) {
      // Wait for DOM to update
      setTimeout(() => {
        const dayElements = document.querySelectorAll('.MuiPickersDay-root');
        if (dayElements.length > 0) {
          dayElements.forEach(element => {
            const dayNum = element.textContent;
            const monthYear = document.querySelector('.MuiPickersCalendarHeader-label').textContent;

            // Parse the displayed date
            const [month, year] = monthYear.split(' ');
            const monthIndex = {
              January: 0,
              February: 1,
              March: 2,
              April: 3,
              May: 4,
              June: 5,
              July: 6,
              August: 7,
              September: 8,
              October: 9,
              November: 10,
              December: 11,
            }[month];

            const currentDay = dayjs()
              .year(Number.parseInt(year))
              .month(monthIndex)
              .date(Number.parseInt(dayNum));

            // Check if this date has messages
            const hasMessages = availableDates.some(d => {
              return d.format('YYYY-MM-DD') === currentDay.format('YYYY-MM-DD');
            });

            // Add indicator if needed
            if (hasMessages) {
              // Remove existing indicator if any
              const existing = element.querySelector('.message-indicator');
              if (existing) existing.remove();

              // Add indicator
              const indicator = document.createElement('div');
              indicator.className = 'message-indicator';
              element.appendChild(indicator);
            }
          });
        }
      }, 100);
    }
  }, [calendarAnchorEl, availableDates]);

  // Wrapper that proxies to ChatService
  const sendMessageViaWebSocket = message => {
    return ChatService.sendMessage(
      selectedChat.id,
      message,
      selectedChat.recipient_details.user_id
    );
  };

  const handleFilePickClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = e => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
      // Generate preview for images
      if (file.type?.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setFilePreviewUrl(url);
      } else {
        setFilePreviewUrl(null);
      }
    }
  };

  const handleClearSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setFilePreviewUrl(null);
  };

  const uploadSelectedFile = async () => {
    if (!selectedFile || !selectedChat) return;
    try {
      setIsUploading(true);
      const apiResponse = await ChatService.uploadChatFile(
        selectedChat.id,
        selectedFile,
        selectedChat.recipient_details.user_id
      );

      // Normalize server response to message shape expected by parent
      const messagePayload = {
        type: 'new_message',
        message_id: apiResponse?.id || apiResponse?.message_id,
        content: apiResponse?.message || '',
        sender_id: selectedChat.teacher_id, // ensure alignment as current user
        recipient_id: apiResponse?.recipient_id || selectedChat.recipient_details.user_id,
        created_at: apiResponse?.created_at || new Date().toISOString(),
        file_url: apiResponse?.file_url,
        file_name: apiResponse?.file_name,
        file_size: selectedFile?.size,
        is_sent: true,
      };

      if (typeof onMessageSent === 'function') {
        onMessageSent(messagePayload);
      } else if (onMessageSent && onMessageSent.scrollToBottom) {
        onMessageSent(messagePayload);
      }

      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl(null);
      setTimeout(() => {
        if (onMessageSent?.scrollToBottom) onMessageSent.scrollToBottom();
      }, 100);
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setIsUploading(false);
      // Fallback: refetch messages to sync positions if backend sets server-side sender
      try {
        const refreshed = await ChatServiceApi.getChatMessages(selectedChat.id);
        if (Array.isArray(refreshed)) {
          // bubble up by emitting last message as a new event to trigger UI state refresh
          const last = refreshed[refreshed.length - 1];
          if (last) {
            const payload = {
              type: 'new_message',
              message_id: last.id || last.message_id,
              content: last.message || last.content || '',
              sender_id: last.sender_id,
              recipient_id: last.recipient_id,
              created_at: last.created_at,
              file_url: last.file_url,
              file_name: last.file_name,
            };
            if (typeof onMessageSent === 'function') onMessageSent(payload);
          }
        }
      } catch (_) {}
    }
  };

  const formatFileSize = bytes => {
    if (!bytes && bytes !== 0) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
    const val = bytes / 1024 ** i;
    return `${val.toFixed(val < 10 && i > 0 ? 1 : 0)} ${sizes[i]}`;
  };

  useEffect(() => {
    if (onMessageSent) {
      onMessageSent.sendFunction = sendMessageViaWebSocket;
      onMessageSent.scrollToBottom = scrollToBottom;
      onMessageSent.openFilePicker = handleFilePickClick;
    }

    return () => {
      if (onMessageSent) {
        delete onMessageSent.sendFunction;
        delete onMessageSent.scrollToBottom;
        delete onMessageSent.openFilePicker;
      }
    };
  }, [selectedChat, onMessageSent]);

  return (
    <Box className="chat-messages">
      {loading ? (
        <Box className="loading-container">
          <ChatSkeleton />
        </Box>
      ) : messages.length === 0 ? (
        <Box className="no-messages">
          <Typography>No messages yet</Typography>
        </Box>
      ) : (
        <>
          {/* Make messages section scrollable without visible scrollbar */}
          <Box className="scrollable-messages" ref={scrollableContainerRef}>
            {/* Render messages grouped by date */}
            {Object.keys(groupedMessages).map(date => (
              <Box
                key={date}
                className="message-date-group"
                ref={el => (messageGroupRefs.current[date] = el)}
              >
                <Box className="date-header">
                  <Typography className="date-header-text">{formatDateHeader(date)}</Typography>
                </Box>

                {groupedMessages[date].map(message => (
                  <Box
                    key={message.id}
                    className={`chat-message ${isCurrentUser(message) ? 'sent' : 'received'}`}
                  >
                    <Box className="chat-avatar-container">
                      {/* Use proper avatar based on sender */}
                      {isCurrentUser(message) ? (
                        currentUser?.profile_image ? (
                          <img
                            src={currentUser.profile_image || '/placeholder.svg'}
                            alt="User Avatar"
                            className="chat-message-avatar"
                          />
                        ) : (
                          <Box className="chat-message-avatar-fallback">
                            {getSenderInitial(message)}
                          </Box>
                        )
                      ) : selectedChat.recipient_details?.profile_image ? (
                        <img
                          src={selectedChat.recipient_details.profile_image || '/placeholder.svg'}
                          alt="User Avatar"
                          className="chat-message-avatar"
                        />
                      ) : (
                        <Box className="chat-message-avatar-fallback">
                          {getSenderInitial(message)}
                        </Box>
                      )}
                    </Box>
                    <Box className="chat-message-wrapper">
                      {(() => {
                        const ext = (message.file_name || '').split('.').pop()?.toLowerCase();
                        const isImageMsg =
                          message.file_url &&
                          ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
                        return (
                          <Box
                            className={`chat-message-content ${isImageMsg ? 'kw-with-image' : ''}`}
                          >
                            {message.message && (
                              <Typography className="chat-message-text">
                                {message.message}
                              </Typography>
                            )}
                            {message.file_url && renderFileMessage(message)}
                          </Box>
                        );
                      })()}
                      <Typography className="chat-message-time">
                        {formatTime(message.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          {/* Calendar */}
          <Popover
            open={Boolean(calendarAnchorEl)}
            anchorEl={calendarAnchorEl}
            onClose={handleCalendarClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                borderRadius: '12px',
                overflow: 'hidden',
              },
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={selectedDate}
                onChange={handleDateSelect}
                componentsProps={{
                  day: {
                    className: 'calendar-day-wrapper',
                  },
                }}
                sx={{
                  // Improved styles for visibility
                  '& .MuiPickersDay-root': {
                    fontSize: '14px',
                    '&.Mui-selected': {
                      border: '2px solid #4caf50',
                      backgroundColor: '#4caf50 !important',
                      color: '#ffffff !important',
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Popover>

          {/* Hidden file input for attachments */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          {/* Attachment tray with icon-only actions */}
          {selectedFile && (
            <Box className="kw-attachment-tray">
              <Box className="kw-attachment-left">
                {filePreviewUrl ? (
                  <img src={filePreviewUrl} alt="preview" className="kw-attachment-thumb" />
                ) : (
                  <Box className="kw-attachment-generic">📄</Box>
                )}
                <Box className="kw-attachment-meta">
                  <Typography className="kw-attachment-name" title={selectedFile.name}>
                    {selectedFile.name}
                  </Typography>
                  <Typography className="kw-attachment-size">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>
              </Box>
              <Box className="kw-attachment-actions">
                {isUploading && <CircularProgress size={18} className="kw-attachment-progress" />}
                <Tooltip title="Send file">
                  <span>
                    <IconButton
                      size="small"
                      onClick={uploadSelectedFile}
                      disabled={isUploading}
                      className="kw-attachment-send"
                    >
                      <img src={SendIconSvg || '/placeholder.svg'} alt="Send" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Cancel">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleClearSelectedFile}
                      disabled={isUploading}
                      className="kw-attachment-cancel"
                    >
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Preview Modal */}
          <Dialog
            open={previewModal.open}
            onClose={handleClosePreview}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '12px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            <DialogContent
              sx={{
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                backgroundColor: '#f5f5f5',
                position: 'relative',
              }}
            >
              {previewModal.fileType === 'image' && (
                <img
                  src={previewModal.fileUrl}
                  alt={previewModal.fileName}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                  }}
                />
              )}

              {previewModal.fileType === 'pdf' && (
                <iframe
                  src={previewModal.fileUrl}
                  title={previewModal.fileName}
                  style={{
                    width: '100%',
                    height: '70vh',
                    border: 'none',
                  }}
                />
              )}

              {previewModal.fileType === 'other' && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px',
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {previewModal.fileName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Preview not available for this file type
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ padding: '12px 16px', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                {previewModal.fileName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={handleClosePreview} variant="outlined">
                  Close
                </Button>
                <Button
                  onClick={handleDownloadFromPreview}
                  variant="contained"
                  sx={{
                    backgroundColor: '#0A5EF3',
                    '&:hover': {
                      backgroundColor: '#0947CC',
                    },
                  }}
                >
                  Download
                </Button>
              </Box>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default ChatWindow;
